import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Linkedin, ArrowRight, ArrowLeft, ChevronRight,
  Database, ShieldCheck, Brain, Compass, FileText, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

/* -------------------- Firm / About -------------------- */
export function AboutPage() {
  usePageMeta({
    title: "The Firm — Luminary Insights & Advisory",
    description: "Luminary is a strategic intelligence and advisory firm built for boards, capital allocators, and operating leaders. We don't sell research — we help leaders decide.",
    path: "/firm",
    keywords: "about Luminary, strategic advisory firm, board advisory, decision intelligence",
  });
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-24 grain" data-testid="about-page">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <Link to="/" className="btn-ghost mb-8 inline-flex" data-testid="about-back">
            <ArrowLeft size={12} /> Home
          </Link>
          <div className="eyebrow">The Firm</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            We don&apos;t sell research.<br />
            <span className="italic font-medium text-[var(--gold)]">We help leaders decide.</span>
          </h1>
          <p className="mt-8 font-display italic text-2xl sm:text-3xl text-[var(--slate)] leading-snug max-w-3xl">
            Luminary Insights &amp; Advisory is a strategic intelligence and advisory firm built for the people
            who make consequential decisions — boards, capital allocators, and operating leaders.
          </p>

          <div className="mt-14 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <h2 className="font-display font-black text-3xl text-[var(--navy)]">Our discipline</h2>
              <div className="mt-4 prose-editorial">
                <p className="dropcap">
                  Luminary brings together specialty research, decision-grade intelligence, and proprietary
                  frameworks — built for the way real boardroom decisions get made: under uncertainty,
                  with imperfect information, and against a clock.
                </p>
                <p>
                  Our practice covers fourteen high-growth industries — specialty chemicals, advanced
                  materials, packaging, energy transition, semiconductors, industrial automation,
                  sustainability, mining and critical minerals, manufacturing, and emerging technology
                  verticals. Across these, we deliver fifteen intelligence modules and operate seven
                  proprietary strategic frameworks.
                </p>
                <p>
                  Every output is sourced, restrained in tone, and editorially reviewed. We build
                  intelligence that survives both board scrutiny and the test of time.
                </p>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="bg-[var(--navy)] text-white p-7 sm:p-8">
                <div className="eyebrow text-[var(--gold)]">At a glance</div>
                <ul className="mt-6 space-y-5 text-[15px]">
                  {[
                    ["12", "Industries covered"],
                    ["15", "Intelligence Modules"],
                    ["7", "Proprietary frameworks"],
                    ["12", "Live Industry Trackers"],
                    ["6", "Luminous AI brief types"],
                  ].map(([n, l]) => (
                    <li key={l} className="flex items-baseline justify-between gap-6 border-b border-white/10 pb-3 last:border-0">
                      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/70">{l}</span>
                      <span className="font-display font-black text-3xl text-[var(--gold)]">{n}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/strategy-session" className="btn-gold mt-8 w-full justify-center" data-testid="about-cta">
                  Schedule Strategy Session <ChevronRight size={14} />
                </Link>
              </div>
            </aside>
          </div>

          {/* What we are / not */}
          <div className="mt-20 grid md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            <div className="bg-white p-8">
              <div className="eyebrow">What we are</div>
              <ul className="mt-5 space-y-3 text-[15px] text-[var(--navy)]">
                {[
                  "A strategic advisory firm with intelligence at our core.",
                  "Specialists in capital allocation, market entry, M&A, and growth strategy.",
                  "Operators of proprietary frameworks across fourteen industries.",
                  "Custodians of evidence — every claim sourced and challengeable.",
                ].map((p, i) => <li key={i} className="border-l-2 border-[var(--gold)] pl-4">{p}</li>)}
              </ul>
            </div>
            <div className="bg-white p-8">
              <div className="eyebrow eyebrow-muted">What we are not</div>
              <ul className="mt-5 space-y-3 text-[15px] text-[var(--slate)]">
                {[
                  "Not a syndicated-report business.",
                  "Not a survey panel.",
                  "Not a generic AI chatbot dressed up as advisory.",
                  "Not in the business of manufactured certainty.",
                ].map((p, i) => <li key={i} className="border-l-2 border-[var(--slate-light)] pl-4">{p}</li>)}
              </ul>
            </div>
          </div>

          {/* Mission */}
          <div className="mt-20 bg-[var(--bg-2)] border-l-4 border-[var(--gold)] p-8 sm:p-10">
            <div className="eyebrow">Our mission</div>
            <p className="mt-4 font-display italic text-2xl sm:text-3xl text-[var(--navy)] leading-snug">
              To compress decades of consulting craft and capital-market intelligence into decisions our
              clients can take to a board — fast, sourced, and unambiguous.
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

/* -------------------- Methodology — with the flow diagram -------------------- */
const METHOD_STEPS = [
  {
    n: "01",
    icon: Database,
    title: "Intelligence Collection",
    desc: "Primary interviews, regulatory filings, transactional data, peer-reviewed research, and patent intelligence — collected against an explicit research design.",
  },
  {
    n: "02",
    icon: ShieldCheck,
    title: "Validation & Sourcing",
    desc: "Every data point traced to source. Conflicting signals reconciled. Assumptions named. Confidence scored.",
  },
  {
    n: "03",
    icon: Brain,
    title: "Intelligence Analysis",
    desc: "Applied through proprietary frameworks — Decision Intelligence, Market Attractiveness, Growth Opportunity, Future Readiness — and our fifteen intelligence modules.",
  },
  {
    n: "04",
    icon: Compass,
    title: "Strategic Assessment",
    desc: "Synthesis under editorial review. We test the thesis, stress the downside, and quantify the optionality.",
  },
  {
    n: "05",
    icon: FileText,
    title: "Recommendations",
    desc: "Board-ready outputs: decision briefs, opportunity scans, investment theses — with named actions and confidence.",
  },
];

function FlowDiagram() {
  return (
    <div className="mt-14" data-testid="methodology-flow">
      {/* Desktop horizontal flow */}
      <div className="hidden lg:grid grid-cols-5 gap-0 relative">
        {METHOD_STEPS.map((s, i) => (
          <div key={s.n} className="relative">
            <div className="bg-white border border-[var(--border)] p-5 h-full hover:border-[var(--navy)] transition-colors">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">Step {s.n}</span>
              </div>
              <s.icon size={22} className="mt-3 text-[var(--navy)]" />
              <h3 className="font-display font-bold text-lg mt-3 text-[var(--navy)] leading-tight">{s.title}</h3>
              <p className="mt-2 text-[13px] text-[var(--slate)] leading-relaxed">{s.desc}</p>
            </div>
            {i < METHOD_STEPS.length - 1 && (
              <div className="absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                <div className="w-6 h-6 bg-[var(--gold)] flex items-center justify-center rotate-0">
                  <ChevronRight size={14} className="text-[var(--navy)]" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mobile vertical flow */}
      <div className="lg:hidden space-y-2">
        {METHOD_STEPS.map((s, i) => (
          <div key={s.n}>
            <div className="bg-white border border-[var(--border)] p-5 flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-11 h-11 bg-[var(--navy)] text-[var(--gold)] font-mono text-[12px] flex items-center justify-center font-bold">
                  {s.n}
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-lg text-[var(--navy)] leading-tight">{s.title}</h3>
                <p className="mt-1 text-[13px] text-[var(--slate)] leading-relaxed">{s.desc}</p>
              </div>
            </div>
            {i < METHOD_STEPS.length - 1 && (
              <div className="flex justify-center py-1">
                <div className="w-6 h-6 bg-[var(--gold)] flex items-center justify-center rotate-90">
                  <ChevronRight size={14} className="text-[var(--navy)]" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MethodologyPage() {
  usePageMeta({
    title: "Methodology — Five-Stage Intelligence Pipeline · Luminary",
    description: "Every Luminary output follows a five-stage intelligence pipeline: Collection → Validation → Analysis → Strategic Assessment → Recommendations.",
    path: "/methodology",
    keywords: "research methodology, intelligence pipeline, strategic advisory methodology",
  });
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-24" data-testid="methodology-page">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6">
          <Link to="/" className="btn-ghost mb-8 inline-flex" data-testid="methodology-back">
            <ArrowLeft size={12} /> Home
          </Link>
          <div className="eyebrow">Methodology</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            Forensic by design.
          </h1>
          <p className="mt-8 font-display italic text-2xl text-[var(--slate)] max-w-3xl">
            Every Luminary output follows a five-stage intelligence pipeline — designed to make the
            evidence visible and the conclusion defensible.
          </p>

          <FlowDiagram />

          {/* Five-source stack */}
          <div className="mt-20 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow">The evidence stack</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
                Five layers of evidence. One conclusion you can defend.
              </h2>
              <p className="mt-5 text-[var(--slate)] leading-relaxed">
                We do not lean on any one source. Every conclusion is triangulated across the five
                layers below, with explicit confidence scoring on the output.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
              {[
                ["Primary Intelligence", "Operating-executive interviews, expert pipeline, customer voice studies — identities protected."],
                ["Regulatory & Corporate Disclosures", "Annual filings, project FIDs, customs data, patent registries, incentive programs."],
                ["Peer-reviewed Research", "Forty-plus industry journals, scientific publications, central bank and policy research."],
                ["Transactional Data", "Trade flows, capacity registries, deal databases, hiring signals, infrastructure throughput."],
                ["Editorial AI Synthesis", "Luminous Intelligence™ briefs — every output reviewed by a practice lead before release."],
              ].map(([t, d]) => (
                <div key={t} className="bg-white p-5">
                  <h3 className="font-display font-bold text-base text-[var(--navy)] leading-tight">{t}</h3>
                  <p className="mt-2 text-[13px] text-[var(--slate)] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Principles */}
          <div className="mt-20 bg-[var(--navy)] text-white p-8 sm:p-12">
            <div className="eyebrow text-[var(--gold)]">Editorial principles</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-4 leading-tight">
              What we will not do.
            </h2>
            <div className="mt-8 grid md:grid-cols-3 gap-px bg-white/10">
              {[
                ["No manufactured certainty.", "Every claim carries a confidence score. We name the boundary of what we know."],
                ["No unsourced output.", "If we can't source it, we won't publish it. The source register travels with the brief."],
                ["No AI as practice lead.", "Luminous AI accelerates the desk. It does not speak with a senior partner's authority."],
              ].map(([t, d]) => (
                <div key={t} className="bg-[var(--navy)] p-6">
                  <CheckCircle2 size={18} className="text-[var(--gold)]" />
                  <h3 className="font-display font-bold text-lg mt-3 text-white leading-tight">{t}</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/strategy-session" className="btn-gold" data-testid="methodology-cta">
                Bring us your decision <ArrowRight size={14} />
              </Link>
              <Link to="/frameworks" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-white inline-flex items-center gap-2 self-center" data-testid="methodology-frameworks">
                Explore our frameworks →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

/* -------------------- Team / Practice Leads -------------------- */
export function TeamPage() {
  const [authors, setAuthors] = useState([]);
  useEffect(() => { api.get("/authors").then((r) => setAuthors(r.data || [])); }, []);
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-24" data-testid="team-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <Link to="/firm" className="btn-ghost mb-8 inline-flex" data-testid="team-back">
            <ArrowLeft size={12} /> Firm
          </Link>
          <div className="eyebrow">Practice Leads</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            The desk.
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--slate)] max-w-3xl">
            The practice leads who own engagements, sign every brief, and front every strategy session.
          </p>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {authors.map((a) => (
              <div key={a.slug} className="border border-[var(--border)] bg-white hover-lift" data-testid={`author-${a.slug}`}>
                {a.photo_url && (
                  <div className="aspect-[4/5] overflow-hidden bg-[var(--navy)]">
                    <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5 sm:p-6">
                  <h3 className="font-display font-bold text-2xl text-[var(--navy)]">{a.name}</h3>
                  <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--gold)] mt-1">{a.title}</div>
                  <p className="mt-4 text-sm leading-relaxed text-[var(--slate)]">{a.bio}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(a.expertise || []).map((e) => (
                      <span key={e} className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--navy)] border border-[var(--border-strong)] px-2 py-1">
                        {e}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2">
                    {a.social?.linkedin && (
                      <a
                        href={a.social.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="w-8 h-8 border border-[var(--border)] flex items-center justify-center hover:bg-[var(--navy)] hover:text-white hover:border-[var(--navy)] transition-colors"
                        aria-label="LinkedIn"
                        data-testid={`linkedin-${a.slug}`}
                      >
                        <Linkedin size={13} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-[var(--border)] text-center">
            <h2 className="font-display font-bold text-3xl text-[var(--navy)]">Want to work with the desk?</h2>
            <Link to="/strategy-session" className="btn-primary mt-6 inline-flex" data-testid="team-cta">
              Schedule a Strategy Session <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

/* -------------------- Press -------------------- */
export function PressPage() {
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-24" data-testid="press-page">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <Link to="/firm" className="btn-ghost mb-8 inline-flex" data-testid="press-back">
            <ArrowLeft size={12} /> Firm
          </Link>
          <div className="eyebrow">Press</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            For the newsroom.
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--slate)] max-w-2xl">
            Quotable spokespeople, embargoed previews, and methodology disclosures — available on request.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            {[
              { topic: "Decision Intelligence", lead: "How AI is reshaping the speed and confidence of board-grade decision-making." },
              { topic: "Specialty Chemicals & Decarbonisation", lead: "Electric crackers, carbon border mechanisms, and the new economics of specialty chemicals." },
              { topic: "Semiconductors & AI Compute", lead: "Advanced-node capacity, HBM tightness, and the binding constraint on AI infrastructure." },
              { topic: "Critical Minerals", lead: "The copper deficit thesis and what it implies for the next decade of energy transition." },
              { topic: "Energy Transition Capital Allocation", lead: "LNG additions, renewable buildouts, and where capital is mispriced today." },
              { topic: "Industrial Automation & Humanoid Robotics", lead: "From pilots to production — the binding constraints and capital paths to scale." },
            ].map((p) => (
              <div key={p.topic} className="bg-white p-6" data-testid={`press-${p.topic.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <div className="eyebrow">{p.topic}</div>
                <p className="font-display italic text-xl mt-3 leading-snug text-[var(--navy)]">&ldquo;{p.lead}&rdquo;</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="press-contact-cta">
              Media inquiries <ArrowRight size={14} />
            </Link>
            <Link to="/methodology" className="btn-secondary" data-testid="press-methodology">
              See our methodology
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
